import React from 'react';
import { Icon } from '@iconify/react';
import playIcon from '@iconify-icons/lucide/play';
import checkIcon from '@iconify-icons/lucide/check';
import lockIcon from '@iconify-icons/lucide/lock';

/**
 * Tarjeta de curso en "Mi aprendizaje" (Academy - Mi aprendizaje.dc.html).
 * Portada de 140px con acento superior de 4px y chip de línea de negocio
 * arriba a la izquierda. Cuerpo con título y, según estado:
 *  - `completed`: badge verde "Completado".
 *  - `inProgress`: barra fina cian + meta + CTA naranja "Continuar".
 *  - defecto (`started` = false): CTA naranja "Comenzar curso".
 *
 * Presentacional: el llamador pasa `progressPct`, `metaLine` y el `onOpen`.
 */
export default function LearningCard({
  title,
  thumbnail,
  businessName,
  businessAccent = 'var(--color-accent)',
  progressPct,
  metaLine,
  completed = false,
  onOpen,
  openLabel = 'Continuar',
  startLabel = 'Comenzar curso',
  completedLabel = 'Completado',
  // Cuando el curso está completado, además del chip de estado se puede
  // ofrecer un CTA para repasarlo. Opt-in: si el llamador no pasa
  // `reviewLabel`, se preserva el comportamiento anterior (solo chip).
  reviewLabel = null,
  // Texto bajo la barra de progreso, del tipo "50% completado". Si no se
  // pasa, se usa el fallback en español para no romper llamadores previos.
  progressLabel = null,
  // Cuando el usuario no tiene acceso al curso (invitado + curso
  // restringido). Muestra un chip candado sobre la portada, atenúa
  // la card y sustituye el CTA por `lockedLabel`. `onOpen` sigue
  // funcionando — el llamador decide a dónde lleva (detalle en
  // "Vista previa", /plans, gate del prospecto, …).
  locked = false,
  lockedLabel = 'Ver información',
}) {
  const hasProgress = typeof progressPct === 'number' && progressPct > 0;
  const isStarted = hasProgress && !completed;

  return (
    <article
      className="learning-card bg-surface overflow-hidden flex flex-col"
      style={{
        border: '1px solid var(--color-line)',
        borderRadius: 16,
        /* Altura 100% para que dentro de un grid con `align-items: stretch`
           todas las tarjetas ocupen el alto de la fila mayor. Combinado
           con `flex-col` + `flex: 1` en el body + `marginTop: auto` en el
           bloque CTA, los botones quedan alineados al pie aunque los
           títulos ocupen distinto número de líneas. */
        height: '100%',
        /* Micro-interacción al hover (ver <style> en el mismo módulo).
           Transición suave de elevación + ligero levantamiento. El
           :hover vive en el <style> global del componente porque los
           estilos inline no soportan pseudo-selectores. */
        transition:
          'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        willChange: 'transform',
      }}
      data-slot="learning-card"
    >
      <style>{`
        .learning-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 14px 32px rgba(16, 28, 54, 0.14);
          border-color: var(--color-line-strong);
        }
        .learning-card .learning-card-thumb {
          transition: transform 320ms ease;
          transform-origin: center;
        }
        .learning-card:hover .learning-card-thumb {
          transform: scale(1.04);
        }
      `}</style>
      <div
        className="relative w-full flex items-center justify-center"
        style={{
          /* 170 (antes 140) para dar más altura a la imagen y que el
             recorte de `object-fit: cover` corte menos info del borde.
             Ratio ~1.4:1 a 240 px de card, cerca del típico 3:2 de
             thumbnails de cursos. */
          height: 170,
          overflow: 'hidden',
          background: thumbnail
            ? undefined
            : 'repeating-linear-gradient(118deg, rgba(16,28,54,.10) 0 11px, rgba(16,28,54,.03) 11px 22px), #E3E9F2',
        }}
      >
        {thumbnail ? (
          <img
            src={thumbnail}
            alt=""
            className="learning-card-thumb w-full h-full"
            /* `cover` llena el slot sin bordes — a cambio recorta si la
               imagen no coincide con el ratio del slot. Para que la info
               importante no se pierda, lo ideal es que las thumbnails
               suban en ratio 3:2 (ej. 720×480). */
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 11,
              color: '#3C475E',
            }}
          >
            portada del curso
          </span>
        )}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: businessAccent,
          }}
        />
        {businessName && (
          <span
            className="text-ink"
            style={{
              /* Chip overlay sobre la portada, esquina inferior-derecha.
                 Fondo blanco translúcido con blur para legibilidad
                 encima de cualquier imagen. */
              position: 'absolute',
              bottom: 10,
              right: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11.5,
              fontWeight: 800,
              borderRadius: 999,
              padding: '5px 11px',
              background: 'rgba(255, 255, 255, 0.96)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              boxShadow: '0 2px 8px rgba(16, 28, 54, 0.18)',
              zIndex: 1,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: businessAccent,
              }}
            />
            {businessName}
          </span>
        )}
        {locked && (
          /* Overlay tenue navy sobre la portada — comunica "no accesible"
             sin ocultar por completo la imagen. El chip "Bloqueado" que
             iba top-right se retiró: era redundante con el CTA inferior
             (`Requiere afiliación` + icono de candado) y ensuciaba la
             esquina ahora que el chip de línea de negocio vive abajo. */
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(11, 20, 36, 0.42)',
            }}
          />
        )}
      </div>

      <div
        className="flex flex-col"
        style={{ padding: '15px 16px 16px', flex: 1 }}
      >
        <h3
          className="text-ink m-0"
          style={{
            fontSize: 17,
            fontWeight: 800,
            lineHeight: 1.25,
            marginBottom: metaLine ? 6 : 12,
          }}
        >
          {title}
        </h3>

        {/* Meta estática del curso (nivel · N lecciones · duración). Se
            muestra siempre bajo el título — antes solo aparecía junto a la
            barra de progreso. */}
        {metaLine && (
          <div
            className="text-ink-2"
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              marginBottom: 14,
            }}
          >
            {metaLine}
          </div>
        )}

        <div style={{ marginTop: 'auto' }}>
          {completed ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: 4,
                  marginBottom: reviewLabel ? 12 : 0,
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12.5,
                    fontWeight: 800,
                    color: 'var(--color-success)',
                    background: '#E6F2EC',
                    borderRadius: 999,
                    padding: '6px 11px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Icon icon={checkIcon} width={13} height={13} aria-hidden="true" />
                  {completedLabel}
                </span>
              </div>
              {reviewLabel && (
                <button
                  type="button"
                  onClick={onOpen}
                  className="w-full flex items-center justify-center gap-s2 cursor-pointer"
                  style={{
                    minHeight: 48,
                    border: 0,
                    borderRadius: 11,
                    background: 'var(--color-accent)',
                    color: 'var(--color-ink)',
                    fontSize: 15,
                    fontWeight: 800,
                  }}
                >
                  <Icon icon={playIcon} width={14} height={14} aria-hidden="true" />
                  {reviewLabel}
                </button>
              )}
            </>
          ) : (
            <>
              {isStarted && (
                <>
                  <div
                    role="progressbar"
                    aria-valuenow={Math.round(progressPct)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={openLabel}
                    style={{
                      height: 3,
                      borderRadius: 999,
                      background: 'var(--color-line)',
                      overflow: 'hidden',
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        borderRadius: 999,
                        background: 'var(--color-secondary)',
                        width: `${Math.max(0, Math.min(100, progressPct))}%`,
                      }}
                    />
                  </div>
                  <div
                    className="text-ink-2"
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      marginBottom: 14,
                    }}
                  >
                    {progressLabel ||
                      `${Math.round(progressPct)}% completado`}
                  </div>
                </>
              )}
              <button
                type="button"
                onClick={onOpen}
                className="w-full flex items-center justify-center gap-s2 cursor-pointer"
                style={{
                  minHeight: 48,
                  border: locked ? '1.5px solid var(--color-line-strong)' : 0,
                  borderRadius: 11,
                  /* Locked: variante outline neutra en vez del naranja
                     accent. Comunica "no accesible" a través del color,
                     no solo del icono. */
                  background: locked
                    ? 'var(--color-surface)'
                    : 'var(--color-accent)',
                  color: 'var(--color-ink)',
                  fontSize: 15,
                  fontWeight: 800,
                }}
              >
                <Icon
                  icon={locked ? lockIcon : playIcon}
                  width={14}
                  height={14}
                  aria-hidden="true"
                />
                {locked ? lockedLabel : isStarted ? openLabel : startLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}
